import { BusinessObjectBase } from './base';
import { termSchema } from '@schemas/business-objects/term';
import type { AlternateStudent } from './alternate-student';
import type { NotAvailable } from './not-available';
import type { Organization } from './organization';
import type { SelectedStudent } from './selected-student';
import type { TermTime } from './term-time';
import type { TermTutor } from './term-tutor';
import type { TestResult } from './test-result';
import type { User } from './user';
import type { CalendarDate, Id, Timezone } from '../base-types';
import type { Attendance } from './attendance';
import type { Curriculum } from './curriculum';
import type { Prescription } from './prescription';
import type { Session } from './session';
import type { Slot } from './slot';
import type { Student } from './student';
import type { StudentAssignment } from './student-assignment';
import type { Tutor } from './tutor';

export interface TermData {
  readonly id: Id;
  readonly name?: string;
  readonly startDate?: CalendarDate;
  readonly endDate?: CalendarDate;
  readonly timezone?: Timezone;
  readonly organizationId: Id;
  readonly organization?: Organization;
  readonly customerAccountManagerId: Id;
  readonly customerAccountManager?: User;
  readonly literacyManagerId: Id;
  readonly literacyManager?: User;
  readonly siteLeaderId: Id;
  readonly siteLeader?: User;
  readonly selectedStudents?: SelectedStudent[];
  readonly alternateStudents?: AlternateStudent[];
  readonly termTutors?: TermTutor[];
  readonly times?: TermTime[];
  readonly notAvailables?: NotAvailable[];
  readonly testResults?: TestResult[];
}

export interface Term extends Readonly<TermData> {}
type TermCreateWithChildrenData = Partial<
  Omit<
    TermData,
    'selectedStudents' | 'alternateStudents' | 'termTutors' | 'times'
  >
> & {
  selectedStudents?: Partial<SelectedStudent>[];
  alternateStudents?: Partial<AlternateStudent>[];
  termTutors?: Partial<TermTutor>[];
  times?: Partial<TermTime>[];
};

export class Term extends BusinessObjectBase {
  static readonly entityName = 'Term' as const;
  static readonly plural = 'terms';
  static schema = termSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<Term[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new Term(data));
  }

  static async findOne(filter?: any): Promise<Term | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new Term(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<Term> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new Term(data);
  }

  static async create(data: Omit<TermData, 'id'>): Promise<Term> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new Term(result);
  }

  static async createMany(data: Omit<TermData, 'id'>[]): Promise<Term[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new Term(item));
  }

  static async createWithChildren(
    data: TermCreateWithChildrenData,
  ): Promise<Term> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new Term(result);
  }

  static async createManyWithChildren(
    data: TermCreateWithChildrenData[],
  ): Promise<Term[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new Term(item));
  }

  static async updateById(id: Id, updates: Partial<TermData>): Promise<Term> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new Term(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }

  // --- Behaviors ---

  async createAttendanceForAllTutors(date: CalendarDate): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${Term.plural}/${(this as any).id}/create-attendance-for-all-tutors`;
    return BusinessObjectBase.post<any>(url, { date });
  }
}
